// src/components/LocationAutoComplete.js
import React, { useEffect, useRef, useState } from "react";
import { loadGooglePlaces } from "../utils/loadGooglePlaces";

/** Env resolver that works in CRA and Vite without weird hacks */
const getMapsKey = () => {
  // Vite
  if (typeof import.meta !== "undefined" && import.meta.env) {
    const k = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || import.meta.env.VITE_MAPS_KEY;
    if (k) return k;
  }
  // CRA
  if (typeof process !== "undefined" && process.env) {
    const k = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || process.env.VITE_GOOGLE_MAPS_API_KEY;
    if (k) return k;
  }
  return "";
};

// Tiny debounce
const useDebounced = (value, delay = 200) => {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
};

export default function LocationAutoComplete({
  apiKey: apiKeyProp,               // <-- NEW: allow passing key directly
  value,
  onChange,
  onSelect,
  placeholder = "Location (Ex. Solidcore)",
  inputClassName = "",
  className = "",
  country,                          // e.g., "us"
  types: typesProp,                 // optional array of types
}) {
  const types = React.useMemo(() => typesProp ?? ["establishment", "geocode"], [typesProp]);

  const apiKey = apiKeyProp ?? getMapsKey();
  const [ready, setReady] = useState(false);
  const [text, setText] = useState(value || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);

  const svcRef = useRef(null);
  const tokenRef = useRef(null);
  const rootRef = useRef(null);

  useEffect(() => {
    // debug: see what the component actually got
    console.log("[Maps] key len:", apiKey ? apiKey.length : 0);
    window.__mapsKey = apiKey;
  }, [apiKey]);

  // Load / init Places (handles already-loaded + loader)
  useEffect(() => {
    let cancelled = false;

    const initFromGlobal = () => {
      tokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
      svcRef.current = new window.google.maps.places.AutocompleteService();
      setReady(true);
    };

    (async () => {
      try {
        // If already on the page (e.g., injected elsewhere), init immediately
        if (window.google?.maps?.places) {
          initFromGlobal();
          return;
        }
        // Otherwise load via our loader (needs a key)
        if (!apiKey) {
          setReady(false);
          return;
        }
        await loadGooglePlaces(apiKey);
        if (!cancelled) initFromGlobal();
      } catch (err) {
        console.error("[LocationAutoComplete] Maps load/init failed:", err);
        setReady(false);
      }
    })();

    return () => { cancelled = true; };
  }, [apiKey]);

  // keep controlled value in sync
  useEffect(() => { setText(value || ""); }, [value]);

  const debounced = useDebounced(text, 220);

  // Fetch predictions
  useEffect(() => {
    if (!ready || !svcRef.current || !debounced || debounced.trim().length < 2) {
      setResults([]);
      return;
    }
    const req = { input: debounced, sessionToken: tokenRef.current };
    if (country) req.componentRestrictions = { country };
    if (types?.length) req.types = types;

    svcRef.current.getPlacePredictions(req, (preds, status) => {
      const OK = window.google?.maps?.places?.PlacesServiceStatus?.OK;
      if (status !== OK || !preds) {
        setResults([]);
        return;
      }
      setResults(preds.slice(0, 6));
      setOpen(true);
    });
  }, [debounced, ready, country, types]);

  const handleSelect = (p) => {
    setText(p.description);
    onChange?.(p.description);
    onSelect?.({ description: p.description, place_id: p.place_id });
    setOpen(false);
    setResults([]);
    try {
      tokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
    } catch {}
  };

  // Close dropdown on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("click", onDoc);
    return () => document.removeEventListener("click", onDoc);
  }, []);

  // Fallback UI when Maps isn't ready
  if (!ready) {
    return (
      <div className={`relative ${className}`}>
        <input
          type="text"
          name="location"
          placeholder={placeholder}
          className="w-full p-2 border rounded"
          value={text}
          autoComplete="off"
          onChange={(e) => {
            const val = e.target.value;
            setText(val);
            onChange?.(val);
          }}
        />
        <div className="absolute right-2 top-2 text-xs text-red-500">Maps off</div>
      </div>
    );
  }

  // Autocomplete UI
  return (
    <div className={`relative ${className}`} ref={rootRef}>
      <input
        type="text"
        name="location"
        placeholder={placeholder}
        className={`w-full px-3 text-sm border rounded-2xl ${inputClassName}`}
        value={text}
        autoComplete="off"
        onFocus={() => results.length && setOpen(true)}
        onChange={(e) => {
          const val = e.target.value;
          setText(val);
          onChange?.(val);
        }}
      />
      {open && results.length > 0 && (
        <div className="absolute z-50 mt-1 w-full rounded-2xl border bg-white shadow-lg overflow-hidden">
          {results.map((p) => (
            <button
              key={p.place_id}
              type="button"
              className="w-full text-left px-3 py-2 hover:bg-blue-50"
              onClick={() => handleSelect(p)}
            >
              <div className="text-sm font-medium text-gray-900">
                {p.structured_formatting?.main_text || p.description}
              </div>
              {p.structured_formatting?.secondary_text && (
                <div className="text-xs text-gray-500">
                  {p.structured_formatting.secondary_text}
                </div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
